/**
 * Språk- och översättningstjänst för Woke News
 * Tillgängliga språk: Engelska ('en' - default), Tyska ('de'), Svenska ('sv')
 */

export const AVAILABLE_LANGUAGES = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'sv', label: 'Svenska', flag: '🇸🇪' }
]

export const TRANSLATIONS = {
  en: {
    // Header
    tagline: 'Curated feed from NYT, The Guardian, SVT, DW, Spiegel etc.',
    searchPlaceholder: 'Search headlines...',
    clearSearch: 'Clear search',
    toggleCompact: 'Toggle compact view',
    toggleStandard: 'Toggle standard view',
    refreshFeed: 'Refresh news feed',
    toggleThemeDark: 'Switch to dark theme',
    toggleThemeLight: 'Switch to light theme',
    language: 'Language',

    // Countries
    international: 'International',
    germany: 'Germany (EN)',
    sweden: 'Sweden',
    allCountries: 'All news',

    // Source filters
    allSources: 'All sources',

    // Feed sections
    lastHour: 'Last hour',
    earlierToday: 'Earlier today',
    earlierNews: 'Earlier news',

    // Feed actions & status
    showingCount: 'Showing {visible} of {total} headlines',
    loadMore: 'Load more (20 more)',
    readAll: 'You have caught up with all {total} headlines',
    footerNote: 'Click a headline to open the article at the source • Opens in new tab',
    emptyTitle: 'No headlines found matching your search or filters.',
    resetFilters: 'Reset filters and search',
    errorTitle: 'Unable to load news feed',
    retry: 'Try again',

    // Live updates & offline
    newArticlesAvailable: '{count} new stories',
    offlineMode: 'Offline mode — displaying saved headlines',
    justUpdated: 'Updated just now',

    // Time ago
    justNow: 'Just now',
    minAgo: '{n} min ago',
    hoursAgo: '{n} hour ago',
    hoursAgoPlural: '{n} hours ago',
    yesterday: 'Yesterday',
    daysAgo: '{n} days ago'
  },
  de: {
    // Header
    tagline: 'Kuratierter Feed von NYT, The Guardian, SVT, DW, Spiegel u.a.',
    searchPlaceholder: 'Schlagzeilen durchsuchen...',
    clearSearch: 'Suche löschen',
    toggleCompact: 'Kompakte Ansicht umschalten',
    toggleStandard: 'Standardansicht umschalten',
    refreshFeed: 'Feed aktualisieren',
    toggleThemeDark: 'Zu dunklem Design wechseln',
    toggleThemeLight: 'Zu hellem Design wechseln',
    language: 'Sprache',

    // Countries
    international: 'International',
    germany: 'Deutschland (EN)',
    sweden: 'Schweden',
    allCountries: 'Alle Nachrichten',

    // Source filters
    allSources: 'Alle Quellen',

    // Feed sections
    lastHour: 'Letzte Stunde',
    earlierToday: 'Heute früher',
    earlierNews: 'Frühere Nachrichten',

    // Feed actions & status
    showingCount: 'Zeige {visible} von {total} Schlagzeilen',
    loadMore: 'Mehr laden (20 weitere)',
    readAll: 'Sie haben alle {total} verfügbaren Schlagzeilen gelesen',
    footerNote: 'Klicken Sie auf eine Schlagzeile, um den Artikel bei der Quelle zu öffnen • Öffnet in neuem Tab',
    emptyTitle: 'Keine Schlagzeilen für diese Suche oder diesen Filter gefunden.',
    resetFilters: 'Filter und Suche zurücksetzen',
    errorTitle: 'Nachrichten-Feed konnte nicht geladen werden',
    retry: 'Erneut versuchen',

    // Live updates & offline
    newArticlesAvailable: '{count} neue Meldungen',
    offlineMode: 'Offline-Modus — gespeicherte Meldungen werden angezeigt',
    justUpdated: 'Gerade aktualisiert',

    // Time ago
    justNow: 'Gerade eben',
    minAgo: 'vor {n} Min.',
    hoursAgo: 'vor {n} Std.',
    hoursAgoPlural: 'vor {n} Std.',
    yesterday: 'Gestern',
    daysAgo: 'vor {n} Tagen'
  },
  sv: {
    // Header
    tagline: 'Kurerat flöde från NYT, The Guardian, SVT, DW, Spiegel m.fl.',
    searchPlaceholder: 'Sök rubriker...',
    clearSearch: 'Rensa sökning',
    toggleCompact: 'Växla till kompakt vy',
    toggleStandard: 'Växla till standardvy',
    refreshFeed: 'Uppdatera nyhetsflödet',
    toggleThemeDark: 'Växla till mörkt tema',
    toggleThemeLight: 'Växla till ljust tema',
    language: 'Språk',

    // Countries
    international: 'Internationellt',
    germany: 'Tyskland (EN)',
    sweden: 'Sverige',
    allCountries: 'Alla nyheter',

    // Source filters
    allSources: 'Alla källor',

    // Feed sections
    lastHour: 'Senaste timmen',
    earlierToday: 'Tidigare idag',
    earlierNews: 'Tidigare nyheter',

    // Feed actions & status
    showingCount: 'Visar {visible} av {total} rubriker',
    loadMore: 'Ladda fler (20 till)',
    readAll: 'Du har läst alla {total} tillgängliga rubriker',
    footerNote: 'Klicka på en rubrik för att läsa artikeln direkt hos källan • Öppnas i ny flik',
    emptyTitle: 'Inga rubriker matchade ditt filter eller sökning.',
    resetFilters: 'Återställ filter och sökning',
    errorTitle: 'Kunde inte hämta nyhetsflödet',
    retry: 'Försök igen',

    // Live updates & offline
    newArticlesAvailable: '{count} nya nyheter',
    offlineMode: 'Offlineläge — visar sparade nyheter',
    justUpdated: 'Uppdaterades nyss',

    // Time ago
    justNow: 'Alldeles nyss',
    minAgo: '{n} min sedan',
    hoursAgo: '{n} timme sedan',
    hoursAgoPlural: '{n} timmar sedan',
    yesterday: 'Igår',
    daysAgo: '{n} dagar sedan'
  }
}

/**
 * Hämta översatt sträng med valfri interpolation
 */
export function t(lang, key, params = {}) {
  const current = TRANSLATIONS[lang] || TRANSLATIONS.en
  let text = current[key] || TRANSLATIONS.en[key] || key

  for (const [k, v] of Object.entries(params)) {
    text = text.replace(new RegExp(`\\{${k}\\}`, 'g'), v)
  }

  return text
}

/**
 * Formatera relativ tid för valt språk
 */
export function formatRelativeTimeLang(dateInput, lang = 'en') {
  if (!dateInput) return ''
  const date = new Date(dateInput)
  if (isNaN(date.getTime())) return ''

  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffSec = Math.floor(diffMs / 1000)
  const diffMin = Math.floor(diffSec / 60)
  const diffHours = Math.floor(diffMin / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffSec < 60) {
    return t(lang, 'justNow')
  } else if (diffMin < 60) {
    return t(lang, 'minAgo', { n: diffMin })
  } else if (diffHours < 24) {
    return diffHours === 1
      ? t(lang, 'hoursAgo', { n: 1 })
      : t(lang, 'hoursAgoPlural', { n: diffHours })
  } else if (diffDays === 1) {
    return t(lang, 'yesterday')
  } else if (diffDays < 7) {
    return t(lang, 'daysAgo', { n: diffDays })
  } else {
    let locale = 'en-US'
    if (lang === 'sv') locale = 'sv-SE'
    else if (lang === 'de') locale = 'de-DE'

    return date.toLocaleDateString(locale, {
      month: 'short',
      day: 'numeric'
    })
  }
}
